import type { StorageKey } from '@/shared/constants/storage-keys';

/**
 * Typed wrapper around `window.localStorage` with JSON (de)serialization.
 *
 * Why:
 *   - Forces every read/write through a `StorageKey` so we can't typo a key.
 *   - JSON in, JSON out — callers pass and receive structured values, not
 *     raw strings.
 *   - Swallows the common failure modes (SSR / Safari private-mode disables
 *     storage; quota exceeded) so call sites stay declarative.
 *
 * The `Session` write path lives in `cookies.ts` (so cookie + localStorage
 * stay in sync). Use this util directly when you need to persist anything
 * else (e.g. UI prefs).
 */
export const localStorageUtil = {
    get<T>(key: StorageKey): T | null {
        if (typeof window === 'undefined') return null;
        try {
            const raw = window.localStorage.getItem(key);
            return raw == null ? null : (JSON.parse(raw) as T);
        } catch {
            return null;
        }
    },

    set<T>(key: StorageKey, value: T): void {
        if (typeof window === 'undefined') return;
        try {
            window.localStorage.setItem(key, JSON.stringify(value));
        } catch {
            // QuotaExceededError, disabled storage in private mode, etc.
            // Silently no-op — the cookie write is still authoritative.
        }
    },

    remove(key: StorageKey): void {
        if (typeof window === 'undefined') return;
        try {
            window.localStorage.removeItem(key);
        } catch {
            // Ignore — same reasons as `set`.
        }
    },
};
